import React from 'react';
import PropTypes from 'prop-types';
import { defineMessages, FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import queryString from 'query-string';
import classNames from 'classnames';

import Products from '../definitions/Products';
import GiftOfferShape from '../definitions/GiftOfferShape';
import AFFILIATE_QUERY_PARAM_KEYS from '../definitions/AffiliateQueryParamKeys';
import noticeError from '../functions/noticeError';
import getGiftOfferTypeAsMessage from '../functions/getGiftOfferTypeAsMessage';
import Form from '../components/Form';
import Checkbox from '../components/Checkbox';
import EmailInput from '../components/EmailInput';
import StringInput from '../components/StringInput';
import PrimaryButton from '../components/PrimaryButton';
import CondensedTheme from '../components/CondensedTheme';
import IconArrow from '../components/icons/IconArrow';
import PageBehaviourButton from '../components/PageBehaviourButton';
import DefaultIntlFormatValues from '../definitions/DefaultIntlFormatValues';


function GiftOfferIncentiveModal({
    giftOffer,
    isReferralEligible = false,
    referralOfferValue = 15,   // default referral offer value is $15

    productType = Products.CREDIT_CARDS,
    title,
    providerName,
    hasNewsletterOffer,

    onAcceptOrDecline,
    className,
}) {
    const intl = useIntl();

    if (!giftOffer && !isReferralEligible) {
        noticeError(new Error('[GiftOfferIncentiveModal] Unexpectedly called without a gift offer or being referral eligible.'), {
            productType,
            title,
            providerName,
        });
        return null;
    }

    // giftOffer is allowed to be empty if isReferralEligible is true
    const isGiftOffer = !!giftOffer;
    const [
        offerTypeString,
        offerAmount,
    ] = isGiftOffer
        ? [
            getGiftOfferTypeAsMessage(giftOffer.offerType, intl),   // offerTypeString: only matters for gift offers
            giftOffer.maxDollarValue,                               // offerAmount: each gift offer could have a different value
        ]
        : [
            null,                                       // offerTypeString: no gift offer, therefore no gift offer type
            null,                                       // offerAmount: referrals have a hard-wired value
        ];

    // this is a local function and exists to serve two, related messages; unfortunately, the French
    // product type text is different between the two messages
    const productTypeString = getProductTypeAsMessage({ productType, intl });
    const productTypeNewsletter = getProductTypeAsMessage({ productType, intl, isNewsletter: true });
    const hasAffiliateId = typeof window !== 'undefined'
        && queryString.parse(window.location.search)[AFFILIATE_QUERY_PARAM_KEYS.affiliateId];

    function handleAcceptGiftOffer(email, usersName) {
        // naive splitting of usersName into first and last; will not be perfect
        // Example output for the input "Bruce Wayne":
        // matches[0] = "Bruce Wayne" (full match)
        // matches[1] = "Bruce" (first name)
        // matches[2] = " Wayne" (space followed by last name)
        // matches[3] = "Wayne" (last name)
        const matches = usersName.match(/(\w+)(\s(.+))?/);
        const firstName = matches[1];
        const lastName = matches?.[3];

        onAcceptOrDecline({ email, isOfferAccepted: true, firstName, lastName });
    }


    return (
        <Container
            className={classNames('rh-display-flex', 'rh-flex-direction-column', 'rh-align-items-center', className)}
        >
            <div className="incentive-form rh-p-1_25 rh-border-radius-8px rh-box-shadow-surround-large rh-bg-coconut">
                <header>
                    <PageBehaviourButton
                        onClick={() => onAcceptOrDecline({ isOfferAccepted: false })}
                        iconComponent={iconProps => (
                            <IconArrow
                                {...iconProps}
                                outlineWidth="0"
                                direction="right"
                            />
                        )}
                        message={getNoThanksMessage({ isGiftOffer, offerTypeString, isReferralEligible })}
                        size="extra-small"
                        className="rh-ml-auto rh-mb-1_5"
                        data-name="base-ui.gift-offer.incentive.no-thanks"
                        data-test-name="offer-incentive-no-thanks"
                    />

                    <div className={classNames(
                        'header-box rh-position-relative rh-p-1',
                        'rh-border-width-1px rh-border-style-solid rh-border-radius-8px',
                        'rh-bg-grape-lightest rh-border-color-grape',
                    )}
                    >
                        <If condition={isGiftOffer || isReferralEligible}>
                            <span className={classNames(
                                'expiry-pill rh-position-absolute rh-display-inline-block rh-text-xs rh-px-0_5',
                                'rh-fg-coconut rh-border-radius-8px rh-bg-grape-dark',
                            )}
                            >
                                <Choose>
                                    <When condition={isGiftOffer}>
                                        <FormattedMessage
                                            id="base-ui.gift-offer.incentive.offer-ends"
                                            defaultMessage="Offer ends "
                                        />
                                        <time
                                            className="rh-display-inline-block"
                                            dateTime={giftOffer.expiryDate}
                                            // prevent hydration warning if server timezone is different from browser’s timezone
                                            suppressHydrationWarning
                                        >
                                            <FormattedDate
                                                value={giftOffer.expiryDate}
                                                year="numeric"
                                                month="short"
                                                day="numeric"
                                            />
                                        </time>
                                    </When>
                                    <Otherwise>
                                        <FormattedMessage
                                            id="base-ui.gift-offer.incentive.referral-bonus"
                                            defaultMessage="Referral bonus"
                                        />
                                    </Otherwise>
                                </Choose>
                            </span>
                        </If>

                        <p className="rh-text-s rh-m-0 rh-mt-0_5 rh-mb-0_25">
                            {title || providerName}
                        </p>

                        <h2
                            className="rh-title-xs rh-m-0 rh-fg-grape-dark"
                            data-test-name="offer-incentive-main-title"
                        >
                            <Choose>
                                <When condition={isGiftOffer && isReferralEligible}>
                                    <FormattedMessage
                                        id="base-ui.gift-and-referral.incentive.main-title"
                                        defaultMessage="Get an exclusive {offerAmount, number, CAD0} {offerType}{newline}Plus get a {referralOfferValue, number, CAD0} referral bonus"
                                        values={{
                                            ...DefaultIntlFormatValues,
                                            offerAmount,
                                            offerType: offerTypeString,
                                            referralOfferValue,
                                        }}
                                    />
                                </When>
                                <When condition={isGiftOffer}>
                                    <FormattedMessage
                                        id="base-ui.gift-offer.incentive.main-title"
                                        defaultMessage="Get an exclusive {offerAmount, number, CAD0} {offerType}"
                                        values={{
                                            offerAmount,
                                            offerType: offerTypeString,
                                        }}
                                    />
                                </When>
                                <Otherwise>
                                    <FormattedMessage
                                        id="base-ui.gift-offer.incentive.referral.main-title"
                                        defaultMessage="Get {referralOfferValue, number, CAD0} when you’re approved for this {productTypeString}!"
                                        values={{ referralOfferValue, productTypeString }}
                                    />
                                </Otherwise>
                            </Choose>
                        </h2>
                    </div>
                </header>

                <div className="rh-mt-1 rh-p-1">
                    <h2 className="rh-title-xs rh-m-0 rh-mt-0_25">
                        <FormattedMessage
                            id="base-ui.gift-offer.incentive.claim-your-offer"
                            defaultMessage="Claim your offer"
                        />
                    </h2>

                    <Choose>
                        <When condition={isGiftOffer}>
                            <p
                                className="rh-text-s rh-mt-0_25 rh-mb-0"
                                data-test-name="claim-details"
                                // eslint-disable-next-line react/no-danger
                                dangerouslySetInnerHTML={{
                                    __html: giftOffer.promoDetailsHTML,
                                }}
                            />
                        </When>
                        <Otherwise>
                            <p
                                className="rh-text-s rh-mt-0_25 rh-mb-0"
                                data-test-name="claim-details"
                            >
                                <FormattedMessage
                                    id="base-ui.gift-offer.incentive.referral.promo-details"
                                    defaultMessage="The referral bonus will be sent via Interac e-Transfer® to the email address below upon approval."
                                />
                            </p>
                        </Otherwise>
                    </Choose>

                    <Form
                        className="rh-display-flex rh-flex-wrap rh-mt-1"
                        onSubmit={(values) => handleAcceptGiftOffer(values.email, values.usersName)}
                    >
                        <div className="rh-display-flex rh-flex-direction-column rh-align-items-center">
                            <NameAndEmailFields
                                hasAffiliateId={hasAffiliateId}
                                // French requires a slightly different product type text for the newsletter checkbox
                                productTypeString={productTypeNewsletter}
                                hasNewsletterOffer={hasNewsletterOffer}
                            />
                        </div>

                        <div
                            className={classNames('button-wrapper rh-display-flex rh-flex-direction-column rh-mt-2',
                                'rh-width-100p rh-align-items-center rh-gap-0_5')}
                        >
                            <PrimaryButton
                                size="small"
                                className="button-get-offer rh-mt-0 rh-mx-1"
                                data-name="gift-card-offer-submit"  // legacy name: '-card-'
                            >
                                <FormattedMessage
                                    id="base-ui.gift-offer.incentive.get-offer"
                                    defaultMessage="Get this offer"
                                />
                            </PrimaryButton>

                            <p className="rh-text-xs rh-m-0 rh-fg-blackberry-light">
                                <FormattedMessage
                                    id="base-ui.gift-offer.incentive.proceed-to-provider"
                                    defaultMessage="Proceed to {providerName}’s secure website"
                                    values={{ providerName }}
                                />
                            </p>
                        </div>
                    </Form>
                </div>
            </div>
        </Container>
    );
}

GiftOfferIncentiveModal.propTypes = {
    giftOffer: PropTypes.shape(GiftOfferShape),
    isReferralEligible: PropTypes.bool,
    referralOfferValue: PropTypes.number,

    productType: PropTypes.oneOf(Object.values(Products)),
    title: PropTypes.string,
    providerName: PropTypes.string.isRequired,
    hasNewsletterOffer: PropTypes.bool,

    onAcceptOrDecline: PropTypes.func.isRequired,
    className: PropTypes.string,
};


function NameAndEmailFields({ hasAffiliateId, hasNewsletterOffer, productTypeString }) {
    return (
        <CondensedTheme>
            <div
                className={classNames('two-up', 'rh-display-flex', 'rh-flex-wrap', 'rh-gap-0_5', 'rh-width-100p', {
                    // we will need more white space below this input field wrapper when the subscribe checkbox is omitted
                    'rh-mb-1_5': hasAffiliateId,
                })}
            >
                <StringInput
                    className="side-by-side rh-my-0"
                    name="usersName"
                    label={(
                        <FormattedMessage
                            id="base-ui.gift-offer.incentive.usersName.label"
                            defaultMessage="Your name"
                        />
                    )}
                    defaultErrorMessage={(
                        <FormattedMessage
                            id="base-ui.gift-offer.incentive.name-validation.empty"
                            defaultMessage="Please enter your name"
                        />
                    )}
                />

                <EmailInput
                    className="side-by-side rh-mt-0 rh-mb-1"
                    name="email"
                    placeholder={''}
                    label={(
                        <FormattedMessage
                            id="base-ui.gift-offer.incentive.email.label"
                            defaultMessage="Your email address"
                        />
                    )}
                />
            </div>

            <If condition={!hasAffiliateId && hasNewsletterOffer}>
                <div className="rh-display-flex rh-align-items-center rh-width-100p rh-mt-1_5 rh-mb-0_5 rh-mx-0_5">
                    <Checkbox
                        className="checkbox-subscribe"
                        name="subscribe"
                        id="subscribe"
                    />
                    { }
                    <label
                        className="rh-text-s rh-ml-1 rh-my-0"
                        htmlFor="subscribe"
                        data-test-name="newsletter-checkbox"
                    >
                        <FormattedMessage
                            id="base-ui.gift-offer.incentive.newsletters"
                            defaultMessage="I’d like to receive Ratehub {productTypeString} offers and newsletters. I may unsubscribe at any time."
                            values={{ productTypeString }}
                        />
                    </label>
                </div>
            </If>
        </CondensedTheme>
    );
}

NameAndEmailFields.propTypes = {
    hasAffiliateId: PropTypes.string,
    hasNewsletterOffer: PropTypes.bool,
    productTypeString: PropTypes.string,
};


/**
 * Get the localized string for the given Ratehub productType.
 * While the English code for PRODUCT_NAME_MESSAGES is redundant, the French translations will be
 * different based on the `isNewsletter` value.
 * @param {object} args - function arguments
 * @param {string} args.productType - a string-based enumeration of Ratehub productTypes
 * @param {import('react-intl').IntlShape} args.intl - internationalization object
 * @param {boolean} args.isNewsletter - true for the newsletter-based message; will modify the message lookup key
 * @returns {string} - localized string for the product type, suited to a specific use
 */
function getProductTypeAsMessage({ productType, intl, isNewsletter = false }) {
    const productTypeDescriptor = PRODUCT_NAME_MESSAGES[`${productType}${isNewsletter
        ? '_newsletter'
        : ''}`];
    if (productTypeDescriptor) {
        return intl.formatMessage(productTypeDescriptor);
    }

    // eslint-disable-next-line @stylistic/max-len
    noticeError(new RangeError(`[getProductTypeAsMessage] unexpected combination of productType/isNewsletter: “${productType}/${isNewsletter}”.`), {
        expecting: `The arguments must combine to create an key that matches one of “${Object.keys(PRODUCT_NAME_MESSAGES).join('”, “')}”.`,
    });
    return productType;
}

function getNoThanksMessage({ isGiftOffer, offerTypeString, isReferralEligible }) {
    if (isGiftOffer && isReferralEligible) {
        return (
            <FormattedMessage
                id="base-ui.gift-and-referral.incentive.no-thanks"
                defaultMessage="Apply without the {offerType} + referral bonus"
                values={{ offerType: offerTypeString }}
            />
        );
    }

    if (isGiftOffer) {
        return (
            <FormattedMessage
                id="base-ui.gift-offer.incentive.no-thanks"
                defaultMessage="Apply without the {offerType}"
                values={{ offerType: offerTypeString }}
            />
        );
    }

    return (
        <FormattedMessage
            id="base-ui.gift-offer.incentive.no-thanks.referral"
            defaultMessage="Apply without the referral bonus"
        />
    );
}

// inserting the product name in the newsletter message requires different syntax in French
const PRODUCT_NAME_MESSAGES = defineMessages({
    [Products.CREDIT_CARDS]: {
        id: 'base-ui.gift-offer.name.credit-cards',
        defaultMessage: 'card',
    },
    [Products.LOANS]: {
        id: 'base-ui.gift-offer.name.loans',
        defaultMessage: 'loan',
    },
    [Products.CREDIT_CARDS + '_newsletter']: {
        id: 'base-ui.gift-offer.name.credit-cards.newsletter',
        defaultMessage: 'card',
    },
    [Products.LOANS + '_newsletter']: {
        id: 'base-ui.gift-offer.name.loans.newsletter',
        defaultMessage: 'loan',
    },
});

const Container = styled.div`
    > .incentive-form {
        max-width: 90%;
        width: 26em;

        .header-box {
            > .expiry-pill {
                padding-top: 1px;
                top: -0.7em;
                left: 1em;
            }
        }

        .two-up {
            .side-by-side {
                flex: 0 1 100%;
            }
        }

        .button-wrapper {
            .button-get-offer {
                max-width: 15rem;
            }
        }
    }
`;

export default GiftOfferIncentiveModal;
